// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMemo } from 'react';
import { encode } from 'uqr';

export interface MagicLinkQrProps {
  /** The magic link to encode. A live patient credential — see the note below. */
  link: string;
  /** Rendered edge length in CSS pixels. The SVG scales; the matrix does not. */
  size?: number;
}

/**
 * A magic link as a scannable QR code (intakeform-preview-mkiz).
 *
 * WHY THIS EXISTS RATHER THAN JUST A COPY BUTTON. A copy button puts the
 * credential on the CLINICIAN's clipboard, which still needs some channel to
 * reach the patient — and the channel a hurried person reaches for is their
 * own phone, a personal messaging app, an email from a staff account. That is
 * an unaudited hop to an unknown destination. A QR transfers straight to the
 * patient's own device with no intermediate copy and no second system.
 *
 * WHY IT LIVES IN apps/web AND NOT packages/ui. It is deliberately not a
 * general-purpose "render any string as a QR" primitive. Everything it is
 * handed is a live patient credential, and keeping it next to the one screen
 * that handles one is what stops it being pointed at something else.
 *
 * THE LINK NEVER LEAVES THE BROWSER. `encode` is pure computation over the
 * string — no network, no remote QR service, no image host (intakeform-
 * preview-8vz). What reaches the DOM is geometry: a path built from the
 * module matrix, never the URL as text. The URL is still readable from the
 * copy field beside this, which is the accepted exposure; this component adds
 * no new one.
 */
export function MagicLinkQr({ link, size = 220 }: MagicLinkQrProps) {
  const { matrixSize, path } = useMemo(() => {
    // `border: 4` is the quiet zone the QR spec requires. uqr defaults to 1,
    // which scanners routinely fail to lock onto against a busy page.
    //
    // Error correction stays at uqr's default L (7%). Higher levels exist for
    // print — dirt, creases, a scuffed label. This is a clean backlit screen
    // being read from 30cm, where the real failure mode is the opposite one:
    // a magic link URL carries a long JWT, so every step up in correction
    // pushes the version up and shrinks each module. Fewer, larger modules
    // scan better here than more, denser, better-protected ones.
    const qr = encode(link, { border: 4 });
    let d = '';
    for (let y = 0; y < qr.size; y += 1) {
      for (let x = 0; x < qr.size; x += 1) {
        if (qr.data[y]?.[x] === true) d += `M${x} ${y}h1v1h-1z`;
      }
    }
    return { matrixSize: qr.size, path: d };
  }, [link]);

  return (
    <svg
      // Fixed black on white, NOT theme tokens. A scanner needs dark-on-light
      // at high contrast; a QR rendered in a dark theme's foreground colour on
      // its surface colour is a decorative square that does not scan. The
      // white rect also paints the quiet zone, which has to be light whatever
      // the page behind it is doing.
      role="img"
      aria-label="QR code for this patient's intake link"
      data-magic-link-qr
      viewBox={`0 0 ${matrixSize} ${matrixSize}`}
      width={size}
      height={size}
      // Without this the renderer antialiases every module edge, which blurs
      // the 1px boundaries the decoder is looking for.
      shapeRendering="crispEdges"
      style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
    >
      <rect width={matrixSize} height={matrixSize} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}
